import React, { useState } from 'react';
import { Grid, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Button } from 'components/ui/button';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { eventsService } from '../../../../core/services/eventsService';
import styles from '../SchedulerSetting.module.scss';

const getApiErrorMessage = (err: unknown, fallback: string): string => {
	if (err && typeof err === 'object') {
		const apiError = err as { error?: string; message?: string };
		return apiError.error || apiError.message || fallback;
	}
	return fallback;
};

interface CreateEventTypeProps {
	backButtonClick: () => void;
	onEventTypeCreated?: () => void;
}

const CreateEventType: React.FC<CreateEventTypeProps> = ({
	backButtonClick,
	onEventTypeCreated,
}) => {
	const { showToast } = useToastContext();
	const [type, setType] = useState({ label: '', isReservation: false });
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!type.label.trim()) {
			setError('Event type is required');
			return;
		}

		setIsSubmitting(true);

		try {
			await eventsService.addEventType({ 
				label: type.label,
				active: true, // Always pass true
				isReservation: type.isReservation,
			});

			onEventTypeCreated?.();

			// Navigate back after a short delay
			setTimeout(() => {
				backButtonClick();
			}, 1500);
		} catch (err: unknown) {
			showToast('error', getApiErrorMessage(err, 'Failed to create event type'));
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.createTimeOffReason}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={backButtonClick}>
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting || !type.label.trim()}
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Create New Event Type</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				<TextField
					label="Event Type"
					name="label"
					placeholder="Enter event type"
					value={type.label}
					onChange={(e) => {
						setType({ ...type, label: e.target.value });
						if (error) {
							setError(null);
						}
					}}
					className={styles.input}
					margin="normal"
					fullWidth
					inputProps={{ maxLength: 200 }}
					error={!!error}
					helperText={error || ''}
				/>

				<FormControlLabel
					control={
						<Checkbox
							checked={type.isReservation}
							onChange={(e) => setType({ ...type, isReservation: e.target.checked })}
							name="isReservation"
						/>
					}
					label="Available for Reservations"
				/>
			</form>
		</div>
	);
};

export default CreateEventType;

